import { CSSProperties, memo } from 'react';

import i18n from '@/core/i18n';
import { cn } from '@/lib/utils';

import { formatHours } from '../../../helpers/format-time-track.helper';

// Same visual language as the time-tracking summary bars (unified-summary.tsx):
// solid time-off color = approved, striped = pending.
const TIME_OFF_COLOR = 'hsl(var(--time-off))';
const APPROVED_SWATCH: CSSProperties = { backgroundColor: TIME_OFF_COLOR };
const PENDING_SWATCH: CSSProperties = {
  backgroundImage: `repeating-linear-gradient(45deg, ${TIME_OFF_COLOR} 0 3px, hsl(var(--track-empty)) 3px 6px)`,
};

interface ApprovalTimeOffsCellProps {
  timeOffHours: number;
  approvedTimeOffHours: number;
  pendingTimeOffHours: number;
  /** Render in a heavier weight (used for top-level project rows). */
  emphasized?: boolean;
}

const TimeOffLine = ({
  hours,
  label,
  swatch,
  emphasized,
}: {
  hours: number;
  label: string;
  swatch: CSSProperties;
  emphasized: boolean;
}) => (
  <span className="flex items-center gap-1.5 whitespace-nowrap leading-tight">
    <span
      aria-hidden="true"
      className="inline-block h-2 w-2 shrink-0 rounded-sm border border-black/5"
      style={swatch}
    />
    <span className={cn('text-xs tabular-nums', emphasized && 'font-medium')}>
      {formatHours(hours)}
    </span>
    <span className="truncate text-[11px] text-muted-foreground">{label}</span>
  </span>
);

/**
 * Time-off hours for aggregate (project/member) rows of the Time-offs column.
 * Approved and pending hours render as separate compact lines with the same
 * solid/striped indicators the time-tracking summary bars use — no hover needed.
 * Zero time-off renders an em dash.
 */
function ApprovalTimeOffsCellComponent({
  timeOffHours,
  approvedTimeOffHours,
  pendingTimeOffHours,
  emphasized = false,
}: ApprovalTimeOffsCellProps) {
  if (timeOffHours === 0) {
    // eslint-disable-next-line i18next/no-literal-string
    return <span className="text-muted-foreground">—</span>;
  }

  return (
    <div className="flex min-w-0 flex-col gap-0.5 py-0.5">
      {approvedTimeOffHours > 0 && (
        <TimeOffLine
          hours={approvedTimeOffHours}
          label={i18n.t('authenticated.timeTracking:approvals.timeOffs.approvedShort')}
          swatch={APPROVED_SWATCH}
          emphasized={emphasized}
        />
      )}
      {pendingTimeOffHours > 0 && (
        <TimeOffLine
          hours={pendingTimeOffHours}
          label={i18n.t('authenticated.timeTracking:approvals.timeOffs.pendingShort')}
          swatch={PENDING_SWATCH}
          emphasized={emphasized}
        />
      )}
    </div>
  );
}

/** Memoized so an unrelated row's selection toggle doesn't re-render every time-offs cell. */
export const ApprovalTimeOffsCell = memo(ApprovalTimeOffsCellComponent);
